import {
  flatten,
  lensPath,
  map,
  pathOr,
  set,
  toPairs,
  trim,
  test,
  when
} from 'ramda';
import { compose } from 'recompose';
import { mkObjectForLoanAppRole as mkObjectMetaByRoleRole } from 'src/utilities/borrowers';
import { OBJECT_META, LABELS, META_DATA, YES_DOWNCASE } from 'src/services/constants';
import { stringifyTheLoanRole } from 'src/utilities/borrowers';
import { getLoanRoleById } from 'src/utilities/borrowers';
import { isNotEmpty } from 'src/utilities/hasValue';

/*
@FIX-ME: In the MultiAccounts component generates numbers as a way to differentiate objects of the same name e.g
Checking Account, Checking Account 2, Checking Account 3 => All these should not have a number when we send the api call.
This is saved in the account type, hence we do a regex and trim to filter the numerics.
* */
export const mkNewAssetsFromInfoObj = (
  formData: any,
  loanApp: any,
  loanRoleId: any
): any => {
  const assets = [];
  const asset = mkAssets('default', loanApp, loanRoleId);

  for (const asst of toPairs(formData)) {
    const type = trim(pathOr('', [0], asst));
    const accountType = test(/\d/, type) ? type.slice(0, -1) : type;

    const institutionName = pathOr(
      '',
      [1, 'Account Institution', 'value'],
      asst
    );
    const number = pathOr('', [1, 'Account Number', 'value'], asst);
    const balance = pathOr('', [1, 'Cash Or MarketValue', 'value'], asst);

    assets.push({
      type: trim(accountType),
      institutionName,
      number,
      balance,
    });
  }
  return set(lensPath(['accounts']), assets, asset);
};

export const mkNewOtherAssets = (
  asst: any,
  loanApp: any,
  loanRoleId: any
): any => {
  const assets: any = [];
  const asset = mkAssets('default', loanApp, loanRoleId);
  const balance = pathOr('', ['Cash Or MarketValue', 'value'], asst);
  const assetType = pathOr('', ['Asset Type', 'value'], asst);
  const loanRole = stringifyTheLoanRole(getLoanRoleById(loanApp, loanRoleId));
  const DescriptionOfAssets = pathOr(
    '',
    ['description_of_assets', 'value'],
    asst
  );
  const subjectToDebt = pathOr('', ['subject_to_debt', 'value'], asst);
  const nameOfOwners = pathOr('', ['names_of_owners', 'value'], asst);
  assets.push({
    type: trim(assetType),
    balance,
    assetType
  });
  const metaDataValue = {
    asset_type_other_description: DescriptionOfAssets,
    subject_to_debt: subjectToDebt,
    name_of_owners: nameOfOwners,
  };

  return compose(
    set(lensPath(['accounts']), assets),
    set(lensPath([OBJECT_META, LABELS, 'assets_status']), 'Other'),
    set(lensPath([OBJECT_META, LABELS, loanRole]), YES_DOWNCASE),
    set(lensPath([META_DATA]), metaDataValue)
  )(asset);
};

export const mkAssets = (
  name: string,
  loanApp: any,
  loanRoleId: any,
  accounts?: any,
  depository?: any,
  metadata?: any,
  assetType: string = ''
) => {
  const updatedAccounts = accounts ? map((account: any) =>
  ({
    id: pathOr('', ['id'], account),
    number: pathOr('', ['number'], account),
    type: pathOr('', ['type'], account),
    status: '',
    balance: pathOr('', ['balance'], account),
    customerId: '',
    institutionId: '',
    institutionName: pathOr('', ['institutionName'], account),
    balanceDate: '',
    createdDate: '',
    typeOfAsset: '',
    descriptionOfAssets: '',
    value: '',
    subjectToDebt: '',
    nameOfOwners: '',
  }), accounts) : undefined

  const loanRole = stringifyTheLoanRole(getLoanRoleById(loanApp, loanRoleId));
  const object_meta = compose(
    set(lensPath([LABELS, loanRole]), YES_DOWNCASE),
    when(
      () => isNotEmpty(assetType),
      set(lensPath([LABELS, 'assets_status']), assetType)
    )
  )(mkObjectMetaByRoleRole(name, loanApp, loanRoleId));
  return {
    type_meta: {
      kind: '',
      api_version: '',
    },
    object_meta: object_meta,
    accounts: updatedAccounts ?? [
      {
        id: '',
        number: '',
        name: '',
        type: '',
        status: '',
        balance: '',
        customerId: '',
        institutionId: '',
        institutionName: '',
        balanceDate: '',
        createdDate: '',
        typeOfAsset: '',
        descriptionOfAssets: '',
        value: '',
        subjectToDebt: '',
        nameOfOwners: '',
      },
    ],
    depository: depository ?? {},
    metadata: metadata ?? {}
  };
};

export const getAssetDetails = (loanApp: any) => {
  //here multiple assets flatten single array with asset id
  const assets = pathOr([], ['spec', 'assests'], loanApp);
  const accounts = map(
    (account: any) =>
      map(
        (item: any) =>
          compose(
            set(lensPath(['parentId']), pathOr('', ['id'], account)),
            set(lensPath(['routing_number']), pathOr('', ['metadata', 'routing_number'], account)))(
              item
            ),
        pathOr([], ['accounts'], account)
      ),
    assets
  );
  return flatten([accounts]);
};
